/* eslint-disable prettier/prettier */
import SearchIcon from '@mui/icons-material/Search'
import SquareIcon from '@mui/icons-material/Square'
import {
  Badge,
  Box,
  Button,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import {
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { useParams } from 'react-router-dom'

import { TABLET_WIDTH_BREAKPOINT } from '../../config/breakPoints'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useSelectPricingMatrix } from '../../hooks/useSelectPricingMatrix/useSelectPricingMatrix'
import {
  NewServiceStatus,
  ServiceItem,
  useTransformServiceItems
} from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import { colorMap, labelMap } from '../../models/ServiceItemStatuses'
import { RO_CLOSED } from '../../models/Status'
import { WindowSizeContext } from '../../providers'
import { RepairOrderContext } from '../../providers/RepairOrderProvider/RepairOrderProvider'
import {
  useGetRepairOrdersQuery,
  useGetServiceItemsQuery
} from '../../redux/api'
import { ServiceInfoStatusEnum } from '../../sdk'
import {
  estimateServiceCardStyle,
  repairOrderServiceCardStyle
} from '../../styles/serviceCard'
import {
  ElevatedTab,
  ElevatedTabPanel,
  ElevatedTabs
} from '../SharedUI/ElevatedTab'
import { SortSelect, SortTypes } from '../SharedUI/SortSelect'
import { ServiceCard } from './ServiceCard'
import { ServiceCardsOfRepairOrder } from './ServiceCardsOfRepairOrder'
import { LookupServiceDialog } from './useLookupServiceDialog/LookupServiceDialog'
import { useCopyService } from './useLookupServiceDialog/useCopyService'
export type ItemTypes = 'labor' | 'parts' | 'fee' | 'discount' | 'sublet'

interface Props {
  serviceItems: ServiceItem[]
  isEstimate: boolean
  repairOrderVehicleId: string
  readOnly: boolean
  statusFilterOption: NewServiceStatus | 'All'
  setStatusFilterOption: (status: NewServiceStatus | 'All') => void
}

export const ServiceTable: FC<Props> = ({
  serviceItems,
  isEstimate,
  readOnly,
  statusFilterOption,
  setStatusFilterOption
}) => {
  const { repairOrder, customer, inventoryParts, vehicle } =
    useContext(RepairOrderContext)

  const { width } = useContext(WindowSizeContext)
  const overTabletBreakpoint = width >= TABLET_WIDTH_BREAKPOINT

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [sortOrder, setSortOrder] = useState<SortTypes>('lastCreated')
  const [lookupServiceDialog, setLookupServiceDialog] = useState(false)

  const { selectedLaborMatrixId } = useSelectPricingMatrix(
    customer?.information?.defaultPriceLevel || '',
    '',
    ''
  )
  const { handleSubmitCopyService: copyService } = useCopyService()

  const handleCopyToService = useCallback(
    (serviceItem: ServiceItem) => {
      if (!repairOrder?.entry?.id || !selectedLaborMatrixId) {
        return
      }
      copyService(
        repairOrder?.entry?.id,
        serviceItem,
        selectedLaborMatrixId,
        () => {},
        () => {}
      )
    },
    [copyService, repairOrder?.entry?.id, selectedLaborMatrixId]
  )

  const handleTabChange: (event: SyntheticEvent, newValue: string) => void = (
    _,
    newValue
  ) => setStatusFilterOption(newValue as NewServiceStatus | 'All')

  const filteredServiceItems = useMemo(() => {
    if (statusFilterOption === 'All') {
      return serviceItems
    } else {
      return serviceItems.filter(({ status }) => status === statusFilterOption)
    }
  }, [statusFilterOption, serviceItems])

  const sortedServiceItems = useMemo(() => {
    const newArr = [...filteredServiceItems]
    if (sortOrder === 'firstCreated') {
      newArr.reverse()
    }
    return newArr
  }, [filteredServiceItems, sortOrder])

  /* previous deferred start */

  const { data: repairOrders, isLoading: loadingRepairOrders } =
    useGetRepairOrdersQuery({
      authorization: accessToken,
      shop: shopId,
      vehicle: repairOrder?.entry!.info!.vehicle
    })

  const { id } = useParams()

  const prevRepairOrders = repairOrders
    ? repairOrders.filter(
        (r) => r.entry!.id !== id && r.entry!.info!.status === RO_CLOSED
      )
    : undefined

  const { data: prevServices, isLoading: loadingServices } =
    useGetServiceItemsQuery(
      {
        authorization: accessToken,
        shop: shopId,
        repairOrder: (prevRepairOrders || []).map((r) => r.entry!.id!)
      },
      { skip: !prevRepairOrders || prevRepairOrders.length === 0 }
    )

  const prevFilteredServices = useMemo(() => {
    if (!prevServices) {
      return undefined
    }

    return prevServices
      .filter((s) => s.entry!.info!.recommendInFutureRepairOrders)
      .filter((s) => s.entry!.info!.status === ServiceInfoStatusEnum.Deferred)
  }, [prevServices])

  const transformedPrevServices = useTransformServiceItems(
    prevFilteredServices,
    repairOrder?.entry?.info?.isEstimate,
    repairOrder?.entry?.info?.vehicle,
    inventoryParts
  )

  const sortedPrevServices = useMemo(() => {
    if (transformedPrevServices) {
      const newArr = [...transformedPrevServices]
      if (sortOrder === 'firstCreated') {
        newArr.reverse()
      }
      return newArr
    }
  }, [sortOrder, transformedPrevServices])

  // keep track of number of deferred services
  const deferredCount = useMemo(() => {
    if (!loadingServices && prevServices) {
      const deferredServices = prevServices
        .filter((s) => s.entry!.info!.recommendInFutureRepairOrders)
        .filter((s) => s.entry!.info!.status === ServiceInfoStatusEnum.Deferred)
      return deferredServices.length
    }
    return 0
  }, [loadingServices, prevServices])

  /* previous deferred end */
  if (!repairOrder) {
    return <></>
  }
  return (
    <>
      {lookupServiceDialog && (
        <LookupServiceDialog setCustomProps={setLookupServiceDialog} />
      )}
      <Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          mb={1}
          spacing={1}
          alignItems="flex-end">
          <SortSelect
            {...{ sortOrder, setSortOrder }}
            options={['lastCreated', 'firstCreated']}
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}>
          {isEstimate && (
            <ElevatedTabs
              value={statusFilterOption}
              variant={overTabletBreakpoint ? 'fullWidth' : 'scrollable'}
              sx={{ flexGrow: 1 }}>
              <ElevatedTab
                label={
                  <Typography
                    variant="subtitle1"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>{`${
                    labelMap[
                      ServiceInfoStatusEnum.Recommended as NewServiceStatus
                    ]
                  } (${
                    serviceItems.filter(
                      (s) => s.status === ServiceInfoStatusEnum.Recommended
                    ).length
                  })`}</Typography>
                }
                value="All"
                iconPosition="start"
                icon={
                  <SquareIcon
                    sx={{
                      color:
                        colorMap[
                          ServiceInfoStatusEnum.Recommended as NewServiceStatus
                        ]
                    }}
                  />
                }
              />
            </ElevatedTabs>
          )}
          {!isEstimate && (
            <ElevatedTabs
              data-testid="ServiceItemStatusTabs"
              value={statusFilterOption}
              onChange={handleTabChange}
              variant={overTabletBreakpoint ? 'fullWidth' : 'scrollable'}
              sx={{ flexGrow: 1 }}>
              <ElevatedTab
                label={
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        flex: '1 1 auto',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>{`View All`}</Typography>
                    <Badge
                      badgeContent={serviceItems.length}
                      sx={{
                        m: 1,
                        mr: serviceItems.length.toString().length + 1,
                        px: '3px'
                      }}
                      color="primary"
                    />
                  </>
                }
                value="All"
              />
              {[
                ServiceInfoStatusEnum.Recommended,
                ServiceInfoStatusEnum.Authorized,
                ServiceInfoStatusEnum.Deferred
              ].map((status) => (
                <ElevatedTab
                  key={status}
                  value={status}
                  label={
                    <>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          flex: '1 1 auto',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                        {`${labelMap[status as NewServiceStatus]}`}
                      </Typography>
                      <Badge
                        badgeContent={
                          status === ServiceInfoStatusEnum.Deferred
                            ? serviceItems.filter((s) => s.status === status)
                                .length + deferredCount
                            : serviceItems.filter((s) => s.status === status)
                                .length
                        }
                        sx={{
                          m: 1,
                          mr:
                            status === ServiceInfoStatusEnum.Deferred
                              ? (
                                  serviceItems.filter(
                                    (s) => s.status === status
                                  ).length + deferredCount
                                ).toString().length + 1
                              : serviceItems
                                  .filter((s) => s.status === status)
                                  .length.toString().length + 1,
                          px: '3px'
                        }}
                        color="primary"
                      />
                    </>
                  }
                  iconPosition="start"
                  icon={
                    <SquareIcon
                      sx={{ color: colorMap[status as NewServiceStatus] }}
                    />
                  }
                />
              ))}
            </ElevatedTabs>
          )}
          {!readOnly && (
            <Tooltip title={'Add Service to order (\u21E7Shift + S)'}>
              <Button
                data-testid="SearchServiceButton"
                startIcon={<SearchIcon />}
                variant="contained"
                onClick={() => setLookupServiceDialog(true)}>
                Service
              </Button>
            </Tooltip>
          )}
        </Stack>
        <ElevatedTabPanel data-testid="SingleEstimateServiceItemTableContainer">
          {statusFilterOption !== ServiceInfoStatusEnum.Deferred ? (
            <ServiceCardsOfRepairOrder
              serviceItems={sortedServiceItems}
              allServiceItems={serviceItems}
              createAuthorizationCallback={() =>
                setStatusFilterOption(ServiceInfoStatusEnum.Authorized)
              }
              {...{ isEstimate, readOnly, vehicle }}
            />
          ) : (
            <>
              <ServiceCardsOfRepairOrder
                serviceItems={sortedServiceItems}
                allServiceItems={serviceItems}
                createAuthorizationCallback={() =>
                  setStatusFilterOption(ServiceInfoStatusEnum.Authorized)
                }
                {...{ isEstimate, readOnly, vehicle }}
              />
              {!loadingRepairOrders &&
                !loadingServices &&
                deferredCount !== 0 && (
                  <>
                    <Box display="flex" alignItems="center">
                      <Box flexGrow={1}>
                        <hr />
                      </Box>
                      <Typography p={1}>Previously Deferred</Typography>
                      <Box flexGrow={1}>
                        <hr />
                      </Box>
                    </Box>
                    {sortedPrevServices &&
                      sortedPrevServices.map((serviceItem) => (
                        <Paper
                          elevation={0}
                          sx={{
                            ...(isEstimate
                              ? estimateServiceCardStyle
                              : repairOrderServiceCardStyle[
                                  serviceItem.status as string
                                ])
                          }}
                          key={serviceItem.id}
                          data-testid="ServiceCard">
                          <ServiceCard
                            serviceItem={serviceItem}
                            handleOrderCart={() => {}}
                            readOnly={true}
                            overrideUpdateTechnician={() =>
                              handleCopyToService(serviceItem)
                            }
                            showPrice={true}
                            createAuthorizationCallback={() => {}}
                          />
                        </Paper>
                      ))}
                  </>
                )}
            </>
          )}
        </ElevatedTabPanel>
      </Box>
    </>
  )
}
